/**
 * iter87.115 — Bulk magic-link modal for the Players tab.
 * iter87.119 — Migrated from a hand-rolled overlay to the shared shadcn
 *              Dialog primitive so focus-trap, escape-key handling, and
 *              stacking (z-50) all match every other modal in the app.
 *              Data-testids preserved for existing UAT scraping.
 *
 * League admin picks a set of crewmates (with emails on file) and this
 * modal calls `/leagues/{id}/participants/bulk-resend-recovery`. The
 * backend loops with per-target error capture; we render success/failure
 * counts + a friendly per-row summary when the batch completes.
 *
 * Quick-filters:
 *   - Select all (crewmates with emails)
 *   - Only unclaimed  (participants whose invite slot was never accepted —
 *     `is_placeholder` is still true. This mirrors the exact signal the
 *     Invites panel already uses to mark an invite "joined": presence of
 *     a username/personal-code claim, not login recency. iter87.207 fix —
 *     this used to check `last_seen_at`/`claimed_at`, two fields that
 *     don't exist anywhere on the Participant model, so every crewmate
 *     showed "unclaimed" regardless of whether they'd actually joined.
 *
 * Non-email crewmates render disabled with a subtle "no email" tag so
 * the admin sees who's missing an address before clicking Send.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, X, Send, CheckSquare, Square, Loader2 } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const isUnclaimed = (p) => !!p?.is_placeholder;

export default function BulkMagicLinkModal({
    leagueId, participants, adminParticipantId, currentParticipantId, onClose,
}) {
    const eligible = useMemo(
        () => (participants || []).filter((p) => !!(p.email || "").trim() && p.id !== adminParticipantId),
        [participants, adminParticipantId],
    );
    const noEmail = useMemo(
        () => (participants || []).filter((p) => !(p.email || "").trim() && p.id !== adminParticipantId),
        [participants, adminParticipantId],
    );
    const [selected, setSelected] = useState(() => new Set());
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => setSelected(new Set(eligible.map((p) => p.id)));
    const selectUnclaimed = () => setSelected(new Set(eligible.filter(isUnclaimed).map((p) => p.id)));
    const clearAll = () => setSelected(new Set());

    const send = async () => {
        if (selected.size === 0) {
            toast.error("Select at least one crewmate first.");
            return;
        }
        setSending(true);
        setResult(null);
        try {
            const r = await api.bulkResendRecovery(leagueId, Array.from(selected));
            setResult(r);
            if (r.sent > 0) {
                toast.success(`Sent join links to ${r.sent} crewmate(s).`);
            }
            if (r.failed > 0) {
                toast.error(`${r.failed} email(s) failed to send — see the summary.`);
            }
            if (r.skipped_no_email > 0) {
                toast.info(`${r.skipped_no_email} crewmate(s) had no email on file.`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk send failed.");
        } finally {
            setSending(false);
        }
    };

    // iter87.119: `onOpenChange(false)` is the Radix contract for the
    // user dismissing via Escape, backdrop click, or the built-in close
    // affordance. We block dismissal while a send is in-flight to
    // preserve the pre-migration behavior.
    const handleOpenChange = (open) => {
        if (!open && !sending) {
            onClose();
        }
    };

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogPortal>
                <DialogOverlay />
                <DialogPrimitive.Content
                    onEscapeKeyDown={(e) => { if (sending) e.preventDefault(); }}
                    onPointerDownOutside={(e) => { if (sending) e.preventDefault(); }}
                    onInteractOutside={(e) => { if (sending) e.preventDefault(); }}
                    className="fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] w-full sm:max-w-lg max-h-[92dvh] p-0 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-200 data-[state=closed]:duration-150"
                    data-testid="bulk-magic-link-modal"
                >
                    <div className="brutal-card w-full max-h-[92dvh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                            <div className="flex items-center gap-2">
                                <Mail className="w-5 h-5 text-cyber" />
                                <DialogPrimitive.Title asChild>
                                    <h3 className="font-display font-extrabold text-base uppercase tracking-wide">
                                        Send join links
                                    </h3>
                                </DialogPrimitive.Title>
                            </div>
                            <DialogPrimitive.Close asChild>
                                <button
                                    type="button"
                                    disabled={sending}
                                    data-testid="bulk-magic-link-close-btn"
                                    className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-40"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </DialogPrimitive.Close>
                        </div>

                        <div className="px-4 pt-3 pb-2 border-b border-zinc-800">
                            <DialogPrimitive.Description asChild>
                                <p className="text-[11px] text-zinc-400 leading-relaxed mb-2">
                                    Each selected crewmate gets a fresh 30-minute magic link so they
                                    can jump straight into the rivalry — no username or code lookup needed.
                                </p>
                            </DialogPrimitive.Description>
                            <div className="flex flex-wrap gap-1.5 items-center">
                                <button
                                    type="button"
                                    onClick={selectAll}
                                    data-testid="bulk-magic-link-select-all-btn"
                                    className="text-[10px] font-bold uppercase px-2 py-1 border border-cyber/60 text-cyber rounded hover:bg-cyber/10"
                                >
                                    Select all ({eligible.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={selectUnclaimed}
                                    data-testid="bulk-magic-link-select-unclaimed-btn"
                                    className="text-[10px] font-bold uppercase px-2 py-1 border border-volt/60 text-volt rounded hover:bg-volt/10"
                                >
                                    Only unclaimed
                                </button>
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    data-testid="bulk-magic-link-clear-btn"
                                    className="text-[10px] font-bold uppercase px-2 py-1 border border-zinc-700 text-zinc-400 rounded hover:text-zinc-200"
                                >
                                    Clear
                                </button>
                                <span className="ml-auto text-[10px] text-zinc-500">
                                    {selected.size} selected
                                </span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="bulk-magic-link-list">
                            {eligible.map((p) => {
                                const checked = selected.has(p.id);
                                const unclaimed = isUnclaimed(p);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => toggle(p.id)}
                                        data-testid={`bulk-magic-link-row-${p.id}`}
                                        className={`w-full flex items-center gap-2 p-2 rounded text-left transition-colors ${
                                            checked ? "bg-cyber/10 border border-cyber/60" : "border border-zinc-800 hover:border-zinc-600"
                                        }`}
                                    >
                                        {checked
                                            ? <CheckSquare className="w-4 h-4 text-cyber flex-shrink-0" />
                                            : <Square className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-white truncate">
                                                {p.username}
                                                {p.id === currentParticipantId && (
                                                    <span className="ml-1 text-[9px] text-zinc-500 font-normal">(you)</span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-zinc-500 truncate">{p.email}</div>
                                        </div>
                                        {unclaimed && (
                                            <span className="text-[9px] font-bold uppercase text-volt whitespace-nowrap">unclaimed</span>
                                        )}
                                    </button>
                                );
                            })}
                            {noEmail.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1">
                                    <p className="text-[10px] uppercase tracking-wide font-bold text-zinc-500">
                                        Missing email ({noEmail.length}) — add one before they can receive a link
                                    </p>
                                    {noEmail.map((p) => (
                                        <div key={p.id} className="flex items-center gap-2 p-2 opacity-60" data-testid={`bulk-magic-link-noemail-${p.id}`}>
                                            <Square className="w-4 h-4 text-zinc-700 flex-shrink-0" />
                                            <span className="text-xs text-zinc-500 truncate flex-1">{p.username}</span>
                                            <span className="text-[9px] uppercase text-zinc-600">no email</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {eligible.length === 0 && noEmail.length === 0 && (
                                <div className="text-xs text-zinc-500 italic p-3" data-testid="bulk-magic-link-empty">
                                    No other crewmates in this rivalry yet.
                                </div>
                            )}
                        </div>

                        {result && (
                            <div className="px-4 py-2 border-t border-zinc-800 text-[10px] space-y-0.5" data-testid="bulk-magic-link-result">
                                <div className="text-cyber">Sent: {result.sent}</div>
                                {result.skipped_no_email > 0 && (
                                    <div className="text-zinc-500">Skipped (no email): {result.skipped_no_email}</div>
                                )}
                                {result.failed > 0 && (
                                    <div className="text-red-400">Failed: {result.failed}</div>
                                )}
                            </div>
                        )}

                        <div className="p-3 border-t border-zinc-800 flex gap-2">
                            <DialogPrimitive.Close asChild>
                                <button
                                    type="button"
                                    disabled={sending}
                                    data-testid="bulk-magic-link-cancel-btn"
                                    className="flex-1 brutal-btn-ghost text-xs py-2"
                                >
                                    Close
                                </button>
                            </DialogPrimitive.Close>
                            <button
                                type="button"
                                onClick={send}
                                disabled={sending || selected.size === 0}
                                data-testid="bulk-magic-link-send-btn"
                                className="flex-1 brutal-btn-primary text-xs py-2 flex items-center justify-center gap-1.5 disabled:opacity-40"
                            >
                                {sending
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
                                    : <><Send className="w-3 h-3" /> Send {selected.size > 0 ? `(${selected.size})` : ""}</>}
                            </button>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>
        </Dialog>
    );
}
